/** Recruitment Network · node cards, edges and the hover inspection card. */
import { memo, useLayoutEffect, useRef, useSyncExternalStore } from "react";
import type { CSSProperties, ReactNode } from "react";
import { createPortal } from "react-dom";
import { Mail, FileSearch, CalendarCheck, Award, Building2, MapPin, Sparkles, Check, X, Mic, Clock } from "lucide-react";
import { initialsOf } from "../../../lib/initials.js";
import { displayTitle } from "../displayTitle.js";
import { getRequisitionStatus } from "../requisitionFormat.js";
import { STATUS_META } from "./networkModel.js";
import type { Box, CandidateStatus, NetCandidate, NetRequisition, StageKey } from "./networkModel.js";

// ---------------------------------------------------------------- hover store
/** Hover lives outside React state: entering a card notifies only the hover card, never the
 *  graph, so hovering across dozens of candidates re-renders nothing else. */
export interface HoverState {
  id: string;
  rect: DOMRect;
}
export function createHoverStore() {
  let value: HoverState | null = null;
  const listeners = new Set<() => void>();
  return {
    get: () => value,
    set(next: HoverState | null) {
      if (next === value || (next === null && value === null)) return;
      value = next;
      listeners.forEach((l) => l());
    },
    subscribe(l: () => void) {
      listeners.add(l);
      return () => listeners.delete(l);
    },
  };
}
export type HoverStore = ReturnType<typeof createHoverStore>;

// ---------------------------------------------------------------- shared bits
const pos = (b: Box): CSSProperties => ({
  transform: `translate(${b.x}px, ${b.y}px)`,
  width: b.w,
  height: b.h,
});

/** Same thresholds the resume list colours by (≥75 strong, ≥50 moderate). */
export function scoreTone(n: number | null): "good" | "warn" | "crit" | "none" {
  if (n == null) return "none";
  if (n >= 75) return "good";
  if (n >= 50) return "warn";
  return "crit";
}

export function ScoreRing({ value, size = 42 }: { value: number | null; size?: number }): ReactNode {
  const r = (size - 6) / 2;
  const c = 2 * Math.PI * r;
  const pct = value == null ? 0 : Math.max(0, Math.min(100, value));
  return (
    <span className={`rn-ring tone-${scoreTone(value)}`} style={{ width: size, height: size }}>
      <svg width={size} height={size} aria-hidden="true">
        <circle cx={size / 2} cy={size / 2} r={r} className="rn-ring-track" />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          className="rn-ring-fill"
          strokeDasharray={`${(pct / 100) * c} ${c}`}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
      </svg>
      <span className="rn-ring-num">
        {value == null ? "—" : Math.round(value)}
        {value != null && <small>%</small>}
      </span>
    </span>
  );
}

/** Which score a candidate card leads with. Once the interview is done (the Interview Completed
 *  stage) the interview result is the more complete signal, so it replaces resume fit; on the
 *  earlier stages — received, resume scanning, interview invitation — resume fit is what exists. */
export function headlineScore(c: NetCandidate, stage: StageKey): { value: number | null; label: string; title: string } {
  if (stage === "completed") {
    return {
      value: c.interviewScore,
      label: "Interview",
      title: c.interviewScore != null ? "Interview score (question-level)" : "Interview not scored yet",
    };
  }
  return { value: c.match, label: "Resume", title: c.match != null ? "Resume fit against this requirement" : "Resume not scored yet" };
}

/** A score ring with a one-word caption, so a card says WHICH score it is showing. */
export function CaptionedScore({ value, label, title, size }: { value: number | null; label: string; title: string; size?: number }): ReactNode {
  return (
    <span className="rn-score" title={title}>
      <ScoreRing value={value} size={size} />
      <small>{label}</small>
    </span>
  );
}

export function candidateSubline(c: NetCandidate): string {
  const parts: string[] = [];
  if (c.years != null) parts.push(`${c.years}y exp`);
  parts.push(STATE_LABEL[c.state ?? ""] ?? (c.match == null ? "Not scored yet" : "Resume scored"));
  return parts.join(" · ");
}

export const STATE_LABEL: Record<string, string> = {
  sourced: "Sourced",
  screened: "Screened",
  shortlisted: "Shortlisted",
  invited: "Invited",
  self_scheduled: "Scheduled",
  reminded: "Reminded",
  interviewing: "Interviewing",
  scored: "Interview scored",
  advanced: "Advanced",
  proposed_cut: "Cut proposed",
  cut_notified: "Notified",
  finalist: "Finalist",
};

// ---------------------------------------------------------------- candidate status
const STATUS_ICON: Record<CandidateStatus, typeof Check> = {
  progressing: Check,
  rejected: X,
  interviewed: Mic,
  pending: Clock,
};

/** A small icon (not text) so status reads at a glance without hovering and without cluttering
 *  the card; the label rides in the title/aria-label. */
export function StatusBadge({ status, withLabel = false }: { status: CandidateStatus; withLabel?: boolean }): ReactNode {
  const Icon = STATUS_ICON[status];
  const meta = STATUS_META[status];
  return (
    <span className={`rn-status-badge st-${status}${withLabel ? " with-label" : ""}`} title={`${meta.label} — ${meta.hint}`} aria-label={meta.label}>
      <Icon size={withLabel ? 12 : 11} strokeWidth={3} />
      {withLabel && <span>{meta.label}</span>}
    </span>
  );
}

// ---------------------------------------------------------------- requisition anchor
export const RequisitionNode = memo(function RequisitionNode({
  box,
  req,
  counts,
  open,
  onOpen,
}: {
  box: Box;
  req: NetRequisition;
  counts: { resumes: number; matches: number; interviews: number };
  open: boolean;
  onOpen: () => void;
}): ReactNode {
  const status = getRequisitionStatus(req.state);
  const title = displayTitle(req.title);
  return (
    <div
      role="button"
      tabIndex={0}
      aria-pressed={open}
      title="View the full job details"
      className={`rn-node rn-req is-active${open ? " is-selected" : ""}`}
      style={pos(box)}
      data-node-id="req"
      data-draggable="1"
      onClick={onOpen}
      onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && (e.preventDefault(), onOpen())}
    >
      <div className="rn-req-head">
        <span className="rn-req-mark">{initialsOf(title, "RQ").slice(0, 2)}</span>
        <div className="rn-req-title">
          <strong title={title}>{title}</strong>
          <span title={req.config.department ?? undefined}>
            {req.jobCode ?? `REQ-${req.id.slice(0, 6).toUpperCase()}`}
            {req.config.department ? ` · ${req.config.department}` : ""}
          </span>
        </div>
        <span className={`rn-status s-${status.toLowerCase()}`}>{status}</span>
      </div>
      <div className="rn-req-stats">
        <div><b className="c-cyan">{counts.resumes}</b><span>Resumes</span></div>
        <div><b className="c-green">{counts.matches}</b><span>AI Matches</span></div>
        <div><b className="c-violet">{counts.interviews}</b><span>Interviews</span></div>
      </div>
      <div className="rn-req-foot">
        <span><Sparkles size={13} /> View job details</span>
        {req.config.location && (
          <span className="rn-muted"><MapPin size={12} /> {req.config.location}</span>
        )}
      </div>
      <span className="rn-port rn-port-r" />
    </div>
  );
});

// ---------------------------------------------------------------- pipeline stage
const STAGE_ICON: Record<StageKey, typeof Mail> = {
  received: Mail,
  scanning: FileSearch,
  invited: CalendarCheck,
  completed: Award,
};

export const StageNode = memo(function StageNode({
  box,
  stage,
  label,
  hint,
  count,
  active,
  onSelect,
}: {
  box: Box;
  stage: StageKey;
  label: string;
  hint: string;
  count: number;
  active: boolean;
  onSelect: (s: StageKey) => void;
}): ReactNode {
  const Icon = STAGE_ICON[stage];
  return (
    <div
      role="button"
      tabIndex={0}
      aria-pressed={active}
      title={hint}
      className={`rn-node rn-stage st-${stage}${active ? " is-active" : ""}`}
      style={pos(box)}
      data-node-id={`stage:${stage}`}
      data-draggable="1"
      onClick={() => onSelect(stage)}
      onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && (e.preventDefault(), onSelect(stage))}
    >
      <div className="rn-stage-head">
        <span className="rn-stage-icon"><Icon size={18} /></span>
        <div className="rn-stage-title">
          <strong title={label}>{label}</strong>
        </div>
        <div className="rn-stage-count">
          <b>{count}</b>
          <span>candidates</span>
        </div>
      </div>
      <div className="rn-stage-foot">
        <span className="rn-dot-label">{active ? "Active" : count ? "Ready" : "Empty"}</span>
        <span className="rn-stage-cta">{active ? "Showing candidates →" : "Click to inspect"}</span>
      </div>
      <span className="rn-port rn-port-l" />
      <span className="rn-port rn-port-r" />
    </div>
  );
});

// ---------------------------------------------------------------- candidate
export const CandidateNode = memo(function CandidateNode({
  box,
  c,
  stage,
  rank,
  selected,
  hover,
  onSelect,
}: {
  box: Box;
  c: NetCandidate;
  stage: StageKey;
  rank: number;
  selected: boolean;
  hover: HoverStore;
  onSelect: (id: string) => void;
}): ReactNode {
  const show = (el: HTMLElement): void => hover.set({ id: c.id, rect: el.getBoundingClientRect() });
  return (
    <div
      role="button"
      tabIndex={0}
      aria-pressed={selected}
      aria-label={`${c.name}, rank ${rank}`}
      className={`rn-node rn-cand is-${c.status}${selected ? " is-selected" : ""}`}
      style={pos(box)}
      data-node-id={`cand:${c.id}`}
      data-draggable="1"
      onClick={() => onSelect(c.id)}
      onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && (e.preventDefault(), onSelect(c.id))}
      onMouseEnter={(e) => show(e.currentTarget)}
      onMouseLeave={() => hover.set(null)}
      onFocus={(e) => show(e.currentTarget)}
      onBlur={() => hover.set(null)}
    >
      <div className="rn-cand-head">
        <span className="rn-avatar">{initialsOf(c.name, "?").slice(0, 2)}</span>
        <div className="rn-cand-title">
          <strong title={c.name}>{c.name}</strong>
          <span>{candidateSubline(c)}</span>
        </div>
        <CaptionedScore {...headlineScore(c, stage)} />
      </div>
      <div className="rn-chips">
        {c.skills.slice(0, 3).map((s) => (
          <span key={s} className="rn-chip">{s}</span>
        ))}
        {!c.skills.length && c.scan && <span className="rn-chip ghost">{c.scan.filename}</span>}
      </div>
      <span className="rn-rank">#{rank}</span>
      <StatusBadge status={c.status} />
      <span className="rn-port rn-port-l" />
    </div>
  );
});

// ---------------------------------------------------------------- constellation
export const HubNode = memo(function HubNode({ box, total, active }: { box: Box; total: number; active: number }): ReactNode {
  return (
    <div className="rn-node rn-hub" style={pos(box)} data-node-id="hub" data-draggable="1">
      <span className="rn-hub-icon"><Building2 size={20} /></span>
      <div>
        <strong>Your hiring network</strong>
        <span>{active} active of {total} requirements</span>
      </div>
    </div>
  );
});

export const ConstellationNode = memo(function ConstellationNode({
  box,
  req,
  focused,
  onOpen,
}: {
  box: Box;
  req: NetRequisition;
  focused: boolean;
  onOpen: (id: string) => void;
}): ReactNode {
  const status = getRequisitionStatus(req.state);
  const title = displayTitle(req.title);
  return (
    <div
      role="button"
      tabIndex={0}
      title="Open this requirement's focused network"
      className={`rn-node rn-star s-${status.toLowerCase()}${focused ? " is-active" : ""}`}
      style={pos(box)}
      data-node-id={`req:${req.id}`}
      data-draggable="1"
      onClick={() => onOpen(req.id)}
      onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && (e.preventDefault(), onOpen(req.id))}
    >
      <div className="rn-star-head">
        <strong title={title}>{title}</strong>
        <span className={`rn-status s-${status.toLowerCase()}`}>{status}</span>
      </div>
      <div className="rn-star-stats">
        <span><b className="c-cyan">{req.scannedCount ?? 0}</b> resumes</span>
        <span><b className="c-green">{req.matchesCount ?? 0}</b> matches</span>
        <span><b className="c-violet">{req.interviewCount ?? 0}</b> interviews</span>
      </div>
    </div>
  );
});

// ---------------------------------------------------------------- edges
export interface EdgeSpec {
  id: string;
  d: string;
  tone: "active" | "selected" | "idle" | "spoke";
}

export const EdgeLayer = memo(function EdgeLayer({ edges, flowing }: { edges: EdgeSpec[]; flowing: boolean }): ReactNode {
  return (
    <svg className={`rn-edges${flowing ? " is-flowing" : ""}`} aria-hidden="true">
      <defs>
        <filter id="rn-glow" x="-20%" y="-20%" width="140%" height="140%">
          <feGaussianBlur stdDeviation="3" result="b" />
          <feMerge>
            <feMergeNode in="b" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>
      {/* idle first so active paths draw on top */}
      {edges
        .slice()
        .sort((a, b) => EDGE_Z[a.tone] - EDGE_Z[b.tone])
        .map((e) => (
          <path key={e.id} d={e.d} className={`rn-edge e-${e.tone}`} />
        ))}
    </svg>
  );
});
const EDGE_Z = { spoke: 0, idle: 1, active: 2, selected: 3 } as const;

// ---------------------------------------------------------------- hover inspection card
/**
 * Portalled to <body> with position:fixed, so no ancestor's overflow or stacking context — the
 * canvas clips, the transformed world creates a stacking context, sibling nodes paint later —
 * can hide it. It opens beside the hovered card (right if there is room, else left) and may
 * cover the neighbouring column; it never takes pointer events, so it can't trap the cursor.
 */
export function HoverCard({
  store,
  byId,
  stage,
}: {
  store: HoverStore;
  byId: Map<string, NetCandidate>;
  stage: StageKey;
}): ReactNode {
  const hover = useSyncExternalStore(store.subscribe, store.get);
  const ref = useRef<HTMLDivElement>(null);
  const c = hover ? byId.get(hover.id) : undefined;

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || !hover) return;
    const W = el.offsetWidth;
    const H = el.offsetHeight;
    const gap = 12;
    const { rect } = hover;
    const left = rect.right + gap + W <= window.innerWidth - 8 ? rect.right + gap : Math.max(8, rect.left - gap - W);
    const top = Math.min(Math.max(8, rect.top - 6), window.innerHeight - H - 8);
    el.style.left = `${left}px`;
    el.style.top = `${top}px`;
  }, [hover]);

  if (!hover || !c) return null;
  const comps = c.scan?.components.filter((x) => x.score != null).slice(0, 4) ?? [];
  return createPortal(
    <div ref={ref} className="rn-portal rn-hovercard" role="tooltip">
      <div className="rn-hc-head">
        <span className="rn-avatar">{initialsOf(c.name, "?").slice(0, 2)}</span>
        <div>
          <strong>{c.name}</strong>
          <span>{c.email ?? "No email on file"}</span>
        </div>
        <CaptionedScore {...headlineScore(c, stage)} size={46} />
      </div>
      <StatusBadge status={c.status} withLabel />
      <div className="rn-hc-facts">
        <div><span>Experience</span><b>{c.years != null ? `${c.years} yrs evidenced` : "—"}</b></div>
        <div><span>Stage</span><b>{STATE_LABEL[c.state ?? ""] ?? (c.match == null ? "Awaiting scan" : "Resume scored")}</b></div>
        <div><span>Resume fit</span><b>{c.match != null ? `${Math.round(c.match)}%` : "—"}</b></div>
        <div><span>Interview</span><b>{c.interviewScore != null ? `${Math.round(c.interviewScore)}%` : "—"}</b></div>
      </div>
      {comps.length > 0 && (
        <div className="rn-hc-bars">
          {comps.map((x) => (
            <div key={x.name} className="rn-bar-row">
              <span>{x.name}</span>
              <b>{Math.round(x.score!)}%</b>
              <i><em className={`tone-${scoreTone(x.score)}`} style={{ width: `${x.score}%` }} /></i>
            </div>
          ))}
        </div>
      )}
      {(c.skills.length > 0 || c.missing.length > 0) && (
        <div className="rn-chips">
          {c.skills.map((s) => <span key={s} className="rn-chip">{s}</span>)}
          {c.missing.map((s) => <span key={s} className="rn-chip miss" title="Must-have not evidenced">{s}</span>)}
        </div>
      )}
      {c.scan?.summary && <p className="rn-hc-summary">{c.scan.summary}</p>}
      <div className="rn-hc-foot">Click to open the full profile</div>
    </div>,
    document.body,
  );
}
